import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';
import BilingualField from '../components/BilingualField';
import ImagePicker from '../components/ImagePicker';

const inputClass = "w-full bg-[#1A1A1A] border border-[rgba(212,175,55,0.2)] rounded-sm px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-colors";
const labelClass = "block text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mb-2";
const defaultBadge = {
  number: '37+',
  label_en: 'Years of Excellence',
  label_ar: 'عاماً من التميز',
  enabled: true,
};

const emptySlide = () => ({
  id: `slide-${Date.now()}`,
  overline_en: '', overline_ar: '',
  title_highlight_en: '', title_highlight_ar: '',
  title_main_en: '', title_main_ar: '',
  description_en: '', description_ar: '',
  cta_en: '', cta_ar: '',
  cta_target: 'about',
  image_url: '',
  enabled: true,
  order: 99,
});

const SlideCard = ({ slide, index, total, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`bg-[#1A1A1A] border rounded-sm overflow-hidden transition-colors ${slide.enabled ? 'border-[rgba(212,175,55,0.2)]' : 'border-white/10 opacity-60'}`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        {slide.image_url && <img src={slide.image_url} alt="" className="w-12 h-9 object-cover rounded-sm flex-shrink-0" />}
        {!slide.image_url && <div className="w-12 h-9 bg-[#0A0A0A] rounded-sm flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{slide.title_highlight_en} {slide.title_main_en || '(untitled)'}</p>
          <p className="text-white/40 text-xs truncate">{slide.overline_en}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={e => { e.stopPropagation(); onChange({ ...slide, enabled: !slide.enabled }); }} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white rounded transition-colors" title={slide.enabled ? 'Disable' : 'Enable'}>
            {slide.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
          <button onClick={e => { e.stopPropagation(); onMoveUp(); }} disabled={index === 0} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white disabled:opacity-20 rounded transition-colors"><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={e => { e.stopPropagation(); onMoveDown(); }} disabled={index === total - 1} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white disabled:opacity-20 rounded transition-colors"><ChevronDown className="w-3.5 h-3.5" /></button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }} className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-red-400 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-[rgba(212,175,55,0.1)] pt-4">
          <BilingualField label="Overline" valueEn={slide.overline_en} valueAr={slide.overline_ar} onChangeEn={v => onChange({ ...slide, overline_en: v })} onChangeAr={v => onChange({ ...slide, overline_ar: v })} />
          <BilingualField label="Title Highlight (white text)" valueEn={slide.title_highlight_en} valueAr={slide.title_highlight_ar} onChangeEn={v => onChange({ ...slide, title_highlight_en: v })} onChangeAr={v => onChange({ ...slide, title_highlight_ar: v })} />
          <BilingualField label="Title Main (gold text)" valueEn={slide.title_main_en} valueAr={slide.title_main_ar} onChangeEn={v => onChange({ ...slide, title_main_en: v })} onChangeAr={v => onChange({ ...slide, title_main_ar: v })} />
          <BilingualField label="Description" type="textarea" valueEn={slide.description_en} valueAr={slide.description_ar} onChangeEn={v => onChange({ ...slide, description_en: v })} onChangeAr={v => onChange({ ...slide, description_ar: v })} />
          <BilingualField label="CTA Button Text" valueEn={slide.cta_en} valueAr={slide.cta_ar} onChangeEn={v => onChange({ ...slide, cta_en: v })} onChangeAr={v => onChange({ ...slide, cta_ar: v })} />
          <div>
            <label className={labelClass}>CTA Target Section</label>
            <select value={slide.cta_target} onChange={e => onChange({ ...slide, cta_target: e.target.value })} className={inputClass}>
              <option value="about">About</option>
              <option value="products">Products</option>
              <option value="contact">Contact</option>
            </select>
          </div>
          <ImagePicker label="Slide Background Image" value={slide.image_url} onChange={v => onChange({ ...slide, image_url: v })} />
        </div>
      )}
    </div>
  );
};

const HeroEditorPage = () => {
  const [heroContent, setHeroContent] = useState({});
  const [slides, setSlides] = useState([]);
  const [autoplay, setAutoplay] = useState(6000);
  const [badge, setBadge] = useState(defaultBadge);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/content').then(r => {
      const hero = r.data?.hero || {};
      setHeroContent(hero);
      setSlides(hero.slides || []);
      setAutoplay(hero.autoplay_interval_ms || 6000);
      setBadge({ ...defaultBadge, ...(hero.badge || {}) });
    }).catch(() => toast.error('Failed to load content'));
  }, []);

  const moveSlide = (index, dir) => {
    const newSlides = [...slides];
    const swapIndex = index + dir;
    if (swapIndex < 0 || swapIndex >= newSlides.length) return;
    [newSlides[index], newSlides[swapIndex]] = [newSlides[swapIndex], newSlides[index]];
    setSlides(newSlides.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const nextHero = {
        ...heroContent,
        slides: slides.map((s, i) => ({ ...s, order: i + 1 })),
        autoplay_interval_ms: autoplay,
        badge,
      };
      await adminApi.patch('/admin/content/hero', nextHero);
      setHeroContent(nextHero);
      toast.success('Hero section saved successfully');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Hero Section</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage the homepage slideshow</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Autoplay */}
      <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5">
        <h2 className="text-sm font-semibold text-white mb-4">Slideshow Settings</h2>
        <div>
          <label className={labelClass}>Autoplay Interval: {autoplay / 1000}s</label>
          <input type="range" min={3000} max={10000} step={500} value={autoplay} onChange={e => setAutoplay(Number(e.target.value))} className="w-full accent-[#D4AF37]" />
          <div className="flex justify-between text-white/30 text-xs mt-1"><span>3s</span><span>10s</span></div>
        </div>
      </div>

      {/* Floating Badge */}
      <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-white">Floating Badge</h2>
            <p className="text-white/40 text-xs mt-0.5">Controls the number badge over the hero image</p>
          </div>
          <button
            type="button"
            onClick={() => setBadge(b => ({ ...b, enabled: b.enabled === false }))}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs border transition-colors ${
              badge.enabled !== false
                ? 'border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.12)] text-[#D4AF37]'
                : 'border-white/10 text-white/40 hover:text-white'
            }`}
          >
            {badge.enabled !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {badge.enabled !== false ? 'Visible' : 'Hidden'}
          </button>
        </div>
        <div>
          <label className={labelClass}>Badge Number</label>
          <input
            value={badge.number || ''}
            onChange={e => setBadge(b => ({ ...b, number: e.target.value }))}
            className={inputClass}
            placeholder="37+"
          />
        </div>
        <BilingualField
          label="Badge Label"
          valueEn={badge.label_en || ''}
          valueAr={badge.label_ar || ''}
          onChangeEn={v => setBadge(b => ({ ...b, label_en: v }))}
          onChangeAr={v => setBadge(b => ({ ...b, label_ar: v }))}
          placeholder="Years of Excellence"
        />
      </div>

      {/* Slides */}
      <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Slides ({slides.length})</h2>
          <button onClick={() => setSlides(s => [...s, emptySlide()])} className="flex items-center gap-2 px-3 py-1.5 bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.3)] text-[#D4AF37] text-xs rounded-sm hover:bg-[rgba(212,175,55,0.2)] transition-colors">
            <Plus className="w-3.5 h-3.5" />Add Slide
          </button>
        </div>
        {slides.length === 0 && <p className="text-white/30 text-sm text-center py-6">No slides yet. Add one above.</p>}
        {slides.map((slide, index) => (
          <SlideCard
            key={slide.id}
            slide={slide}
            index={index}
            total={slides.length}
            onChange={updated => setSlides(s => s.map((sl, i) => i === index ? updated : sl))}
            onDelete={() => setSlides(s => s.filter((_, i) => i !== index))}
            onMoveUp={() => moveSlide(index, -1)}
            onMoveDown={() => moveSlide(index, 1)}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroEditorPage;
